<template>
    <div v-bind:class="sampleBatchWrapperClasses"
         v-bind:style="sampleBatchWrapperStyle"
         @click.stop.prevent="handleClick"
         v-on:mouseenter="handleMouseEnter"
         v-on:mouseleave="handleMouseLeave">
        <div class="corner-info" v-if="isBusy">
            <v-tooltip bottom v-if="data.daysLeft !== null">
                <template v-slot:activator="{ on }">
                    <span v-on="on" class="days-left" v-if="data.daysLeft !== null">{{data.daysLeft}}d</span>
                </template>
                <span>Days left</span>
            </v-tooltip>
            <v-tooltip bottom v-if="data.priority">
                <template v-slot:activator="{ on }">
                    <i v-on="on" class="fas fa-bolt high-priority" v-if="data.priority"></i>
                </template>
                <span>High priority sample</span>
            </v-tooltip>
        </div>
        <div class="position">{{position}}</div>
        <div class="sample-title" v-if="data.fullTitle"><b>{{data.fullTitle}}</b></div>
        <div class="sample-title" v-else>empty</div>
        <div class="case-comment comment-text-truncate" v-if="data.caseComment">{{data.caseComment}}</div>
        <div class="specialist-comment comment-text-truncate" v-if="data.specialistComment">{{data.specialistComment}}</div>
        <v-menu
                v-if="showMenu"
                v-model="showMenu"
                :position-x="menuX"
                :position-y="menuY"
                :close-on-content-click="false"
                absolute
                offset-y
                max-width="400"
        >
            <v-card>
                <v-card-text class="text--primary">
                    <div class="cell-title">
                        <div style="display: inline-block;">Cell #<b>{{position}}</b></div>
                        <div class="shift-buttons" v-if="shiftButtonsVisible">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn outlined tile x-small
                                           color="red" :disabled="!canMoveLeft || progress('shiftSamplesToLeft')"
                                           :loading="progress('shiftSamplesToLeft')"
                                           v-on="on" @click="handleShiftLeftButtonClick">
                                        <v-icon small>fas fa-arrow-left</v-icon>
                                    </v-btn>
                                </template>

                                <span>Shift left</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn class="ml-2" outlined tile x-small
                                           color="red" :disabled="!canMoveRight || progress('shiftSamplesToRight')"
                                           :loading="progress('shiftSamplesToRight')"
                                           v-on="on" @click="handleShiftRightButtonClick">
                                        <v-icon small>fas fa-arrow-right</v-icon>
                                    </v-btn>
                                </template>

                                <span>Shift right</span>
                            </v-tooltip>
                        </div>
                    </div>
                    <div v-if="isBusy">
                        <div class="sample-detail-client-title" v-if="data.clientTitle !== null">{{data.clientTitle}}</div>
                        <div class="sample-detail-title"><router-link :to="{ name: 'updatePgtCase', params: { caseSystemId: data.caseSystemId.toString() }}">{{data.fullTitle}}</router-link></div>
                        <div class="sample-detail-patient-name">{{data.patientName}}</div>
                        <v-divider v-if="data.caseComment || data.specialistComment" class="mt-3 mb-3"></v-divider>
                        <div v-if="data.caseComment"><b>Case comment</b>: {{data.caseComment}}</div>
                        <div v-if="data.specialistComment"><b>Specialist comment</b>: {{data.specialistComment}}</div>
                    </div>

                    <v-divider v-if="showDivider" class="mt-3 mb-3"></v-divider>

                    <v-autocomplete
                            ref="caseSelector"
                            auto-select-first
                            autofocus
                            v-model="caseModel"
                            :items="caseItems"
                            :search-input.sync="caseSearch"
                            hide-no-data
                            hide-selected
                            item-text="systemId"
                            item-value="id"
                            label="Case"
                            placeholder="Start typing case title"
                            :menu-props="{ contentClass: 'case-selector' }"
                            clearable
                            @input="handleCaseChange"
                            return-object
                    >
                        <template v-slot:item="{ index, item }">
                            <v-list-item-content>
                                <v-list-item-title>{{item.systemId}}</v-list-item-title>
                                <v-list-item-subtitle>
                                    <span class="mr-2">{{item.patientName}}</span><br/>
                                    <span v-if="item.patientBirthday">
                                        <v-icon color="gray" small>fas fa-birthday-cake</v-icon> {{item.patientBirthday}}
                                    </span>
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </v-autocomplete>
                    <v-autocomplete
                            :disabled="!caseModel || isNoSamples"
                            v-model="sampleModel"
                            :items="caseModel && filterSamples(caseModel) || []"
                            ref="samplesAutocomplete"
                            hide-selected
                            item-text="title"
                            item-value="id"
                            label="Sample"
                            :placeholder="sampleSelectorPlaceholder"
                            clearable
                            no-data-text="No available samples"
                            @input="handleSampleChange"
                            return-object
                    ></v-autocomplete>
                    <!--                    <v-btn color="primary" block :disabled="!caseModel || !sampleModel || actionInProgress"-->
                    <!--                           @click="handleClickSetSampleButton"-->
                    <!--                           :loading="actionInProgress">Set sample-->
                    <!--                    </v-btn>-->

                    <v-divider v-if="showDivider" class="mt-3 mb-3"></v-divider>

                    <div v-if="isBusy">
                        <v-btn block
                               :disabled="removeDisabled"
                               :loading="actionInProgress"
                               color="error"
                               @click="handleRemoveSampleButtonClick">Clear
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
    import _ from 'lodash'
    import NotificationService from '../../services/notificationService'
    import api from '../../store/modules/batches/api'
    import {mapActions, mapGetters} from 'vuex'
    import * as SampleTargets from '../../dictionaries/sample-targets'
    import * as SampleStatuses from '../../dictionaries/sample-statuses'
    import tinygradient from 'tinygradient'

    const completionDateFarColor = '#b6e0ab'
    const completionDateHalfwayColor = '#fed871'
    const completionDateNearColor = '#ffa39e'

    const completionDateNormalColor = '#ade0c9'
    const completionDateOverduedColor = '#ff7488'

    const gradientGenerator = tinygradient(completionDateFarColor, completionDateHalfwayColor, completionDateNearColor);
    const completionDateGradients = gradientGenerator.rgb(7)
    let completionDateColors = []

    for (let i = completionDateGradients.length - 1; i >= 0; i--) {
        completionDateColors.push(completionDateGradients[i].toString('hex6'))
    }

    export default {
        name: 'SampleBatch',
        props: {
            batch: Object,
            position: Number,
            data: Object,
            currentCaseId: String,
            casesWithMissingSamples: Array,
            canMoveLeft: Boolean,
            canMoveRight: Boolean,
        },
        data() {
            return {
                showMenu: false,
                menuX: 0,
                menuY: 0,
                caseModel: null,
                sampleModel: null,
                caseSearch: null,
                caseItems: this.casesWithMissingSamples,
                mouseOnCurrentSample: false,
                sampleAutocompleteMenu: false,
            }
        },
        watch: {
            caseSearch(input) {
                this.searchCase(input)
            },
            showMenu(value) {
                if (!value) {
                    return
                }

                setTimeout(() => {
                    this.$nextTick(this.$refs['caseSelector'].focus)
                }, 200)
            }
        },
        computed: {
            ...mapGetters('batches', {
                    progress: 'progress'
                }
            ),
            shiftButtonsVisible() {
                return this.isBusy
            },
            actionInProgress() {
                return this.progress('addSampleToBatch') || this.progress('updateSampleInBatch') || this.progress('removeSampleFromBatch')
            },
            isBusy() {
                return !!this.data.id
            },
            sampleBatchWrapperClasses() {
                return {
                    'sample-batch-wrapper': true,
                    'busy': this.isBusy,
                    'highlighted': this.currentCaseId && this.currentCaseId === this.data.caseId && !this.mouseOnCurrentSample
                }
            },
            sampleBatchWrapperStyle() {
                const daysLeft = this.data.daysLeft

                if (this.data.analysisComplete) {
                    return {
                        'background-color': '#ffffff'
                    }
                }

                if (daysLeft === null || _.isUndefined(daysLeft)) {
                    return {}
                }

                let color = completionDateOverduedColor

                if (daysLeft >= 7) {
                    color = completionDateNormalColor
                } else if (daysLeft < 0) {
                    color = completionDateOverduedColor
                } else {
                    color = completionDateColors[daysLeft]
                }

                return {
                    'background-color': color
                }
            },
            showDivider() {
                return this.isBusy
            },
            isNoSamples() {
                let samples = _.get(this.caseModel, "samples", [])

                return samples.length === 0
            },
            sampleSelectorPlaceholder() {
                if (this.isNoSamples && this.caseModel) {
                    return 'No samples available'
                }

                return 'Start typing sample title'
            },
            removeDisabled() {
                return this.actionInProgress || this.data.status !== SampleStatuses.InWork || this.batch.status !== 'preparing'
            }
        },
        methods: {
            ...mapActions('batches', {
                shiftSamplesToLeft: 'shiftSamplesToLeft',
                shiftSamplesToRight: 'shiftSamplesToRight',
                addSample: 'addSample',
                updateSample: 'updateSample',
                removeSample: 'removeSample'
            }),
            handleClick(e) {
                this.menuX = e.clientX
                this.menuY = e.clientY
                this.$nextTick(() => {
                    this.showMenu = true
                })
            },
            searchCase: _.debounce(async function (searchStr) {
                if (!searchStr || searchStr.length === 0) {
                    return
                }

                try {
                    this.caseItems = await api.searchCase(searchStr)
                } catch (e) {
                    NotificationService.error('Unable to find cases. Try again later')
                }
            }, 500),
            handleCaseChange(c) {
                if (!c) {
                    this.sampleModel = null
                }

                this.$nextTick(() => {
                    this.$refs['samplesAutocomplete'].focus();
                    this.$refs['samplesAutocomplete'].activateMenu()
                })
            },
            handleSampleChange(s) {
                this.setSample()
            },
            async setSample() {
                let result = false
                if (this.data.id) {
                    result = await this.updateSampleInt()
                } else {
                    result = await this.addSampleInt()
                }

                if (!result) {
                    return
                }

                this.reset()
            },
            reset() {
                this.showMenu = false
                this.menuX = 0
                this.menuY = 0
                this.caseModel = null
                this.sampleModel = null
                this.caseSearch = null
                this.caseItems = []
            },
            async addSampleInt() {
                try {
                    await this.addSample({
                        sampleId: this.sampleModel.id,
                        batchId: this.batch.id,
                        position: this.position
                    })

                    return true

                } catch (e) {
                    console.log(`Unable to add sample: ${e.message}`)
                }
                return false
            },
            async updateSampleInt() {
                try {
                    await this.updateSample({
                        id: this.data.id,
                        sampleId: this.sampleModel.id,
                        position: this.position
                    })

                    return true
                } catch (e) {
                    console.log(`Unable to update sample: ${e.message}`)
                }

                return false
            },
            async handleRemoveSampleButtonClick() {
                try {
                    await this.removeSample({
                        position: this.position,
                        sampleId: this.data.sampleId,
                        batchId: this.batch.id
                    })
                    this.reset()
                    return
                } catch (e) {
                    console.log(`Unable to remove sample from batch: ${e.message}`)
                }
            },
            handleMouseEnter() {
                if (!this.isBusy) {
                    return
                }

                this.mouseOnCurrentSample = true
                this.$emit('change-current-case', this.data.caseId)
            },
            handleMouseLeave() {
                if (!this.isBusy) {
                    return
                }

                const self = this

                self.mouseOnCurrentSample = false
                this.$emit('change-current-case', null)
            },
            async handleShiftLeftButtonClick() {
                const result = await this.shiftSamplesToLeft({
                    sampleBatchId: this.data.id,
                    batchId: this.data.batchId
                })

                if (result) {
                    this.reset()
                }
            },
            async handleShiftRightButtonClick() {
                const result = await this.shiftSamplesToRight({
                    sampleBatchId: this.data.id,
                    batchId: this.data.batchId
                })

                if (result) {
                    this.reset()
                }
            },
            filterSamples(c) {
                return _.filter(c.samples, (item) => {
                    return item.target !== SampleTargets.Archive && (!item.batchId || item.batchId === this.batch.id)
                })
            }
        }
    }
</script>

<style scoped>
    .sample-batch-wrapper {
        /*display: inline-block;*/
        padding: 5px;
        margin: 3px;
        width: 80px;
        height: 60px;
        border: 1px solid black;
        background-color: #c7c7c7;
        cursor: pointer;
        position: relative;
        line-height: 1rem;
        letter-spacing: 0;
    }

    .busy {
        background-color: #ade0c9;
    }

    .sample-batch-wrapper:hover {
        background-color: #aaaaaa !important;
    }

    .highlighted {
        background-color: rgba(147, 58, 180, 0.52) !important;
    }

    .position {
        font-size: 10px;
    }

    .sample-title {
        font-size: 11px;
        line-height: 1;
        overflow-wrap: normal;
    }

    .sample-detail-title {
        font-size: 18px;
        font-weight: 900;
        margin-bottom: 5px;
    }

    .sample-detail-patient-name {
        font-weight: 400;
        font-size: 16px;
    }

    .sample-detail-client-title {
        font-size: 12px;
        /*color: #555555;*/
    }

    .case-comment {
        font-size: 10px;
        margin: 2px 0 0 0;
        padding: 0;
    }

    .specialist-comment {
        font-size: 10px;
        margin: 0;
        padding: 0;
    }

    .cell-title {
        margin-bottom: 15px;
    }

    .shift-buttons {
        display: inline-block;
        float: right;
        margin-top: -12px;
    }

    .high-priority {
        color: red;
        font-size: 10px;
        margin-left: 3px;
    }

    .corner-info {
        position: absolute;
        top: -3px;
        left: -2px;
        width: 100%;
        text-align: right;
    }

    .days-left {
        font-size: 10px;
        font-weight: 500;
    }

    .comment-text-truncate {
        white-space: nowrap!important;
        overflow: hidden!important;
        text-overflow: ellipsis!important;
        line-height: 1.1!important;
    }

</style>

<style>
    .case-selector .v-list__tile {
        height: 70px !important;
    }

    .shift-buttons i {
        font-size: 17px;
    }

    .shift-buttons button {
        min-width: 32px;
    }
</style>